import { Link } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import { useApi } from "@/hooks/useApi";

interface Project {
  id: string;
  title: string;
  description: string | null;
  stage: string;
  status: string;
  progress: number;
  due_date: string | null;
  tech_stack: string[];
}

export default function DashboardProjects() {
  const { data: projects, loading } = useApi<Project[]>("/projects");

  const stageLabels: Record<string, string> = {
    kickoff: "Kickoff",
    design: "Design",
    development: "Development",
    testing: "Testing",
    launch: "Launch",
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>My Projects</h1>
          <p className="text-silver text-sm mt-1">Track the progress of all your active projects.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {projects?.map((project) => (
            <Link
              key={project.id}
              to={`/dashboard/projects/${project.id}`}
              className="bg-dark-card border border-dark-border rounded-2xl p-5 hover:border-electric-blue/30 transition-all group"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <h3 className="text-white font-semibold text-sm group-hover:text-electric-blue transition-colors">{project.title}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${
                  project.status === "active" ? "text-cyber-green border-cyber-green/30 bg-cyber-green/10" :
                  project.status === "paused" ? "text-cyber-orange border-cyber-orange/30 bg-cyber-orange/10" :
                  "text-electric-blue border-electric-blue/30 bg-electric-blue/10"
                }`}>
                  {project.status}
                </span>
              </div>
              <p className="text-silver-dim text-xs mb-4 line-clamp-2">{project.description || "No description."}</p>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs px-2 py-1 rounded-lg bg-electric-blue/10 border border-electric-blue/20 text-electric-blue">{stageLabels[project.stage] || project.stage}</span>
                {project.due_date && (
                  <span className="text-xs text-silver-dim">Due {new Date(project.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                )}
              </div>
              <div className="w-full bg-dark-border rounded-full h-1.5">
                <div className="bg-electric-blue h-1.5 rounded-full transition-all" style={{ width: `${project.progress}%` }} />
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-silver-dim text-xs">Progress</span>
                <span className="text-electric-blue text-xs font-bold">{project.progress}%</span>
              </div>
            </Link>
          ))}
          {(!projects || projects.length === 0) && (
            <div className="col-span-full bg-dark-card border border-dark-border rounded-2xl p-10 text-center">
              <i className="ri-folder-line text-4xl text-silver-dim mb-3 block" />
              <p className="text-silver text-sm">No projects yet.</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

